import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

// Types
export interface ScheduleInterval {
  id: string;
  scheduleId: string;
  agentId: string;
  date: string;
  intervalStart: number;
  intervalEnd: number;
  activityType: string;
  channel?: string;
  isAIGenerated: boolean;
  isOptimized: boolean;
  createdAt: string;
  updatedAt: string;
  agent: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    employeeId?: string;
  };
}

export interface CreateScheduleIntervalDto {
  scheduleId: string;
  agentId: string;
  date: Date | string;
  intervalStart: number;
  intervalEnd: number;
  activityType: string;
  channel?: string;
  isAIGenerated?: boolean;
  isOptimized?: boolean;
}

export interface CoverageAnalytics {
  hourly: Array<{
    hour: string;
    required: number;
    actual: number;
  }>;
}

// API Service
const scheduleIntervalsApi = {
  getScheduleIntervals: async (params: {
    scheduleId?: string;
    date?: string;
    agentId?: string;
    activityType?: string;
    channel?: string;
  }): Promise<ScheduleInterval[]> => {
    const queryParams = new URLSearchParams();
    if (params.scheduleId) queryParams.append('scheduleId', params.scheduleId);
    if (params.date) queryParams.append('date', params.date);
    if (params.agentId) queryParams.append('agentId', params.agentId);
    if (params.activityType) queryParams.append('activityType', params.activityType);
    if (params.channel) queryParams.append('channel', params.channel);

    const response = await api.get(`/schedule-intervals?${queryParams.toString()}`);
    return response.data;
  },

  createScheduleInterval: async (data: CreateScheduleIntervalDto): Promise<ScheduleInterval> => {
    const response = await api.post('/schedule-intervals', data);
    return response.data;
  },

  bulkCreateScheduleIntervals: async (intervals: CreateScheduleIntervalDto[]): Promise<{ created: number }> => {
    const response = await api.post('/schedule-intervals/bulk', { intervals });
    return response.data;
  },

  updateScheduleInterval: async (id: string, updates: Partial<CreateScheduleIntervalDto>): Promise<ScheduleInterval> => {
    const response = await api.patch(`/schedule-intervals/${id}`, updates);
    return response.data;
  },

  deleteScheduleIntervals: async (ids: string[]): Promise<{ deleted: number }> => {
    const response = await api.delete('/schedule-intervals', { data: { ids } });
    return response.data;
  },

  getCoverageAnalytics: async (params: {
    scheduleId: string;
    date: string;
    channel?: string;
  }): Promise<CoverageAnalytics> => {
    const queryParams = new URLSearchParams();
    queryParams.append('scheduleId', params.scheduleId);
    queryParams.append('date', params.date);
    if (params.channel) queryParams.append('channel', params.channel);

    const response = await api.get(`/schedule-intervals/coverage-analytics?${queryParams.toString()}`);
    return response.data;
  },
};

// React Query Hooks

/**
 * Get schedule intervals for a schedule
 */
export function useScheduleIntervals(params: {
  scheduleId?: string;
  date?: Date;
  agentId?: string;
  activityType?: string;
  channel?: string;
  enabled?: boolean;
}) {
  const query = useQuery({
    queryKey: ['schedule-intervals', params],
    queryFn: () =>
      scheduleIntervalsApi.getScheduleIntervals({
        scheduleId: params.scheduleId,
        date: params.date?.toISOString().split('T')[0],
        agentId: params.agentId,
        activityType: params.activityType,
        channel: params.channel,
      }),
    enabled: (!!params.scheduleId || !!params.agentId) && (params.enabled !== false),
  });

  return query;
}

/**
 * Get coverage analytics for a schedule
 */
export function useCoverageAnalytics(params: {
  scheduleId?: string;
  date?: Date;
  channel?: string;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ['coverage-analytics', params.scheduleId, params.date?.toISOString().split('T')[0], params.channel],
    queryFn: () =>
      scheduleIntervalsApi.getCoverageAnalytics({
        scheduleId: params.scheduleId!,
        date: params.date!.toISOString().split('T')[0],
        channel: params.channel,
      }),
    enabled: !!params.scheduleId && !!params.date && (params.enabled !== false),
    refetchInterval: 30000, // Refetch every 30 seconds for near-real-time updates
  });
}

/**
 * Create a single schedule interval
 */
export function useCreateScheduleInterval() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateScheduleIntervalDto) =>
      scheduleIntervalsApi.createScheduleInterval(data),
    onSuccess: (_, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['schedule-intervals'] });
      queryClient.invalidateQueries({ queryKey: ['coverage-analytics', variables.scheduleId] });
    },
  });
}

/**
 * Bulk create schedule intervals
 */
export function useBulkCreateScheduleIntervals() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (intervals: CreateScheduleIntervalDto[]) =>
      scheduleIntervalsApi.bulkCreateScheduleIntervals(intervals),
    onSuccess: (_, variables) => {
      // Get unique schedule IDs from intervals
      const scheduleIds = [...new Set(variables.map((i) => i.scheduleId))];

      // Invalidate queries for all affected schedules
      queryClient.invalidateQueries({ queryKey: ['schedule-intervals'] });
      scheduleIds.forEach((scheduleId) => {
        queryClient.invalidateQueries({ queryKey: ['coverage-analytics', scheduleId] });
      });
    },
  });
}

/**
 * Update a schedule interval
 */
export function useUpdateScheduleInterval() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<CreateScheduleIntervalDto> }) =>
      scheduleIntervalsApi.updateScheduleInterval(id, updates),
    onSuccess: (data) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['schedule-intervals'] });
      queryClient.invalidateQueries({ queryKey: ['coverage-analytics', data.scheduleId] });
    },
  });
}

/**
 * Delete schedule intervals
 */
export function useDeleteScheduleIntervals() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) =>
      scheduleIntervalsApi.deleteScheduleIntervals(ids),
    onSuccess: () => {
      // Invalidate all interval queries
      queryClient.invalidateQueries({ queryKey: ['schedule-intervals'] });
      queryClient.invalidateQueries({ queryKey: ['coverage-analytics'] });
    },
  });
}
